<template>
  <div class="club-points-calendar">
    <div class="club-points-calendar__points-at-date">
      <div class="club-points-calendar__at-date" v-html="pointsAtTitle" />
      <div class="club-points-calendar__points">
        {{ totalBonusesAtSelectedDate | price }} {{ totalBonusesAtSelectedDate | decl('бонус', ['', 'а', 'ов']) }}
      </div>
    </div>

    <div
      ref="widget-wrap"
      class="club-points-calendar__widget-wrap"
    >
      <vue-client-only>
        <div v-if="adaptive.isMobile"
          class="club-points-calendar__widget-month-title"
        >
          {{ mobileMonthName }}
        </div>
        <div
          v-else
          ref="desktop-month-title-desktop-wrap"
          class="club-points-calendar__widget-month-title-desktop-wrap"
        >
          <div
            ref="desktop-month-title-desktop"
            class="club-points-calendar__widget-month-title-desktop"
          >
            <div
              :style="getWidth(days)"
              class="club-points-calendar__widget-month-title txt-overflow"
              :key="key"
              v-for="(days, key) in monthsDays"
            >
              {{ keyToMonthName(key) }}
            </div>
          </div>
        </div>
      </vue-client-only>


      <div
        ref="days-wrap"
        class="club-points-calendar__widget-days-wrap"
        @scroll="onScroll"
      >
        <div
          ref="bonuses"
          class="club-points-calendar__widget-bonuses"
        >
          <club-calendar-points
            v-for="(promo, index) in sortedPromos"
            :key="index"
            :class="['club-points-calendar__widget-bonus-item', `promo-points-${index}`]"
            :type="getPointsType(promo)"
            :title="promo.name"
            :points="promo.bonuses"
            :starts-earlier="isPointsStartsEarlier(promo.dateBegin)"
            :ends-sooner="isPointsEndsSooner(promo.dateEnd)"
            :start="promo.dateBegin"
            :end="promo.dateEnd"
          />
        </div>
        <div ref="widget-month-wrap" class="club-points-calendar__widget-month-wrap">
          <div
            ref="day"
            v-for="(date, index) in timeLine"
            :key="index"
            class="club-points-calendar__widget-day"
            :class="{ 'club-points-calendar__widget-day_selected': isSelected(date) }"
            :data-year="date.getFullYear()"
            :data-month="date.getMonth()"
            :data-day="date.getDate()"
            :data-key="makeKey(date)"
            @click="selectDate(date)"
          >
            <div
              :class="['club-points-calendar__widget-day-title', {
                'club-points-calendar__widget-day-title_last-day': isLastMonthDay(date),
              }]"
            >
              <div :class="['club-points-calendar__widget-day-name', {
                'club-points-calendar__widget-day-name_weekend': isWeekend(date)
              }]"
              >
                {{ getDayOfWeek(date) }}
              </div>
              <div class="club-points-calendar__widget-day-number">
                {{ getDayNumber(date) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { ddmmyyyy } from '@/utils/date-to-string';
import ClubCalendarPoints from './club-calendar-points.vue';
import clearTimeFromDate from '@/utils/clear-time-from-date';
import VueClientOnly from 'vue-client-only';

const WEEK_DAYS_MAP = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const MONTH_NAME_MAP = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];
const MONTH_NAME_MAP_OBJ = {
  '00':'январь',
  '01':'февраль',
  '02':'март',
  '03':'апрель',
  '04':'май',
  '05':'июнь',
  '06':'июль',
  '07':'август',
  '08':'сентябрь',
  '09':'октябрь',
  '10':'ноябрь',
  '11':'декабрь',
};

const defaultFrom = new Date();
const defaultTo = new Date();
defaultTo.setDate(defaultTo.getDate() + 31);

export default {
  name: "club-points-calendar",

  components: { ClubCalendarPoints, VueClientOnly },

  inject: ['adaptive'],

  data() {
    return {
      from: defaultFrom,
      to: defaultTo,
      monthsShown: {},
      selectedDate: defaultFrom,
    }
  },

  computed: {
    ...mapState('club',{
      IS_EMPLOYEE: 'isEmployee',
    }),
    ...mapGetters({
      PROMOS: 'user/promos',
      CASHBACK_BONUS: 'user/cashbackBonus',
      promoBonusesAtDate: 'user/promoBonusesAtDate'
    }),
    // выносим кэшбэк на самый верх, остальное сортируем по дате начала
    sortedPromos() {
      const [cashback, other] = this.PROMOS.reduce((acc, p) => {
        if (p.bonusType === 'cashback') {
          acc[0].push(p);
        } else {
          acc[1].push(p);
        }

        return acc;

        }, [[],[]]);
      const otherSortedByStart = other.sort((a,b) => {
        return new Date(a.dateBegin) - new Date(b.dateBegin);
      })

      return [...cashback, ...otherSortedByStart];
    },
    account() {
      return this.$store.state.user.account
    },
    // массив выводимых дат
    timeLine() {
      let i = new Date(this.from);
      const timeLine = [];

      while (i < this.to) {
        timeLine.push(new Date(i));
        i.setDate(i.getDate() + 1);
      }

      return timeLine;
    },
    // собирает денные о кол-ве дней в каждом выводимом месяце
    // нужно для вывода названий месяцев на десктопе
    // и вычислении ширины для каждого
    monthsDays() {
      return this.timeLine
        .reduce((acc, i) => {
          const month = i.getMonth()
            .toString()
            .padStart(2, '0');

          if (!acc[month]) {
            acc[month] = 0
          }

          acc[month] += 1;

          return acc;
        }, {});
    },
    // иформирует строку для вывода названий месяца на мобиле
    mobileMonthName() {
      return Object
        .entries(this.monthsShown)
        .filter(([, value]) => value.length > 0)
        .reduce((acc,[key]) => {
          const [month] = key.split('-');

          acc.push(MONTH_NAME_MAP[month]);

          return acc
        },[])
        .join(' | ');
    },
    totalDays() {
      return this.timeLine.length;
    },
    fomattedSelectedDate() {
      return ddmmyyyy(this.selectedDate);
    },
    // суммируем бонусы подходящие под дату
    totalBonusesAtSelectedDate() {
      const promoTotal = this.promoBonusesAtDate(this.selectedDate);
      const mainPoints = this.CASHBACK_BONUS.amount || 0;

      return promoTotal + mainPoints;
    },
    mainBonusesTitle() {
      return this.IS_EMPLOYEE ? 'Бонусы сотрудника' : 'Бонусы за покупки';
    },
    pointsAtTitle() {
      const todayDate = ddmmyyyy(new Date());

      if( ddmmyyyy(this.selectedDate) === todayDate ) {
        return `На сегодня вам доступно`;
      }

      return `На <b>${this.fomattedSelectedDate}</b> вам будет доступно`;
    },
  },

  watch: {
    PROMOS: {
      handler() {
        if (this.$isServer) return;

        this.$nextTick(this.onResize);
      },
      immediate: true,
    },
  },

  mounted() {
    this.initMonthsShown();
    this.setupObserver();
    window.addEventListener('resize', this.onResize, true);
    this.onResize();
  },

  beforedestroy() {
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    // инициализация отображаемых месяцев для мобилы
    initMonthsShown() {
      this.timeLine.forEach(i => {
        const year = i.getFullYear();
        const month = i.getMonth();
        const key = `${month}-${year}`;

        if (!this.monthsShown[key]) {
          this.$set(this.monthsShown, key, []);
        }
      });
    },
    isLastMonthDay(date) {
      return date.getDate() === new Date(date.getFullYear(), date.getMonth()+1, 0).getDate();
    },
    // вычисляет ширину для конкретного блока имя месяца
    getWidth(monthDays) {
      return `width:${monthDays / this.totalDays * 100}%;`;
    },
    keyToMonthName(key) {
      return MONTH_NAME_MAP_OBJ[key];
    },
    onScroll(e) {
      if (!this.adaptive.isMobile) {
        this.$refs['desktop-month-title-desktop-wrap'].scrollLeft = e.target.scrollLeft;
      }
    },
    onResize() {
     if (!this.$refs['day'].length) return;

      this.setupBonusesWrapStyles();
      const daysWrap = this.$refs['days-wrap'];

      if (!daysWrap) return;

      this.setupWidgetWrap();
    },
    getDayOfWeek(date) {
      return WEEK_DAYS_MAP[date.getDay()];
    },
    getDayNumber(date) {
      return date
        .getDate()
        .toString()
        .padStart(2, '0');
    },
    makeKey(date) {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();

      return `${day}-${month}-${year}`;
    },
    // вызывается когда при прокрутке появляется день
    dayShowed(entries) {
      entries.forEach(i => {
        const { month, year, key } = i.target.dataset;
        const monthKey = `${month}-${year}`;

        // считаем сколько дней какого месяца показываем для вывода имен месяцев на мобиле
        if (i.isIntersecting) {
          this.monthsShown[monthKey].push(key);
        } else {
          this.monthsShown[monthKey] = this.monthsShown[monthKey].filter(dayKey => {
            return dayKey !== key;
          });
        }
      });
    },
    selectDate(date) {
      this.selectedDate = date;
    },
    isSelected(date) {
      return this.selectedDate.getTime() === date.getTime();
    },
    // устанавливает наблюдение за нодами дней
    setupObserver() {
      const intersectOptions = (ctx) => ({
        root: ctx.$refs['days-wrap'],
        rootMargin: '0px',
        threshold: 0,
      });

      let observer = new IntersectionObserver(this.dayShowed, intersectOptions(this));
      let targets = this.$refs['day'];

      if (!targets) return;

      targets.forEach(i => {
        observer.observe(i);
      });
    },
    // устанавливает ширину для бонусов исходя из размера ноды дней и их кол-ва
    setupBonusesWrapStyles() {
      if (!this.$refs['day'] || !this.adaptive.isMobile) return;

      const days = this.$refs['day'];
      const daysLength = days.length;
      const dayWidth = days[0].offsetWidth;

      this.$refs.bonuses.style.width = `${dayWidth * daysLength}px`;
    },
    // задаем высоту виджету исходя из высоты бонусов и блока с именами месяцев
    setupWidgetWrap() {
      const points = document.querySelectorAll('.club-points-calendar__widget-bonus-item');
      const titleHeight = document.querySelector('.club-points-calendar__widget-day-title').offsetHeight;
      const pointsOffset = 15;
      let totalHeight = 0;
      let additionalHeight = 42;

      if (this.adaptive.isDesktop) {
        additionalHeight = 95;
      } else if (this.adaptive.isTablet) {
        additionalHeight = 107;
      }

      points.forEach(p => {
        totalHeight += p.offsetHeight + pointsOffset;
      });
      totalHeight -= pointsOffset;

      this.$refs['widget-month-wrap'].style.height = `${totalHeight + titleHeight + additionalHeight}px`;
    },
    isWeekend(date) {
      const day = date.getDay();

      return day === 0 || day === 6;
    },
    isPointsStartsEarlier(beginDate) {
      return clearTimeFromDate(this.from).getTime() > clearTimeFromDate(new Date(beginDate)).getTime();
    },
    isPointsEndsSooner(endDate) {
      return clearTimeFromDate(this.to).getTime() < clearTimeFromDate(new Date(endDate)).getTime();
    },
    getPointsType(promo) {
      if (this.IS_EMPLOYEE) {
        return 'employee';
      }

      return promo.bonusType;
    },
  },
}
</script>

<style lang="scss">
  .club-points-calendar {
    &__points-at-date {
      @include breakpoint("tablet") {
        margin-bottom: 24px;
      }
    }

    &__at-date {
      font-size: 12px;
      line-height: 30px;

      @include breakpoint("tablet") {
        display: inline;
        font-size: 14px;
      }

      @include breakpoint("desktop") {
        font-size: 16px;
      }
    }

    &__points {
      font-size: 18px;
      line-height: 24px;
      color: $blue;
      font-weight: 600;
      margin-bottom: 20px;

      @include breakpoint("tablet") {
        display: inline;
        font-size: 14px;
        line-height: 24px;
      }

      @include breakpoint("desktop") {
        font-size: 16px;
      }
    }

    &__widget-wrap {
      box-shadow: 0 2px 8px rgba(0, 0, 0, .04),
                  0 8px 24px rgba(0, 0, 0, .03),
                  0 16px 32px rgba(0, 0, 0, .02);
      border-radius: 4px;
      background: $white;
      border: 1px solid $gray_lu;
      display: flex;
      flex-direction: column;
    }

    &__widget-days-wrap {
      display: flex;
      overflow: auto hidden;
      @include breakpoint("tablet") {
        overflow: hidden;
      }
      flex: 1;
      position: relative;
    }

    &__widget-bonuses {
      position: absolute;
      top: 36px;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;

      @include breakpoint("tablet") {
        top: 44px;

        // для того чтобы перебить стили width в атрибуте style(нужны для мобилы)
        min-width: 100%;
        max-width: 100%;
      }
    }

    .club-calendar-points {
      pointer-events: all;
    }

    &__widget-month-title {
      font-size: 14px;
      line-height: 38px;
      text-align: center;
      text-transform: capitalize;
      font-weight: bold;
    }

    &__widget-month-title-desktop-wrap {
      overflow: hidden;
    }

    &__widget-month-title-desktop {
      display: flex;
    }

    &__widget-month-title-desktop &__widget-month-title {
      position: relative;

      @include breakpoint("tablet") {
        &::after {
          position: absolute;
          content: '';
          display: block;
          border-right: 1px solid $gray_lu;
          top: 0;
          right: 0;
          bottom: 0;
        }

        &:last-child::after {
          border-right: 0;
        }
      }
    }

    &__widget-day-name {
      color: $gray_l;
      font-size: 10px;
      text-transform: uppercase;

      &_weekend {
        color: $rose_l;
      }
    }

    &__widget-day_selected &__widget-day-name {
      font-weight: 600;
      color: $blue;

      @include breakpoint("tablet") {
        font-weight: normal;
        color: $gray_l;

        &_weekend {
          color: $rose_l;
          font-weight: normal;
        }
      }
    }

    &__widget-day_selected &__widget-day-number {
      font-weight: 600;
      color: $blue;
      height: 21px;
    }

    &__widget-day-number {
      font-size: 16px;
      height: 22px;
    }

    &__widget-day {
      text-align: center;
      flex: 1 0 40px;
      cursor: pointer;
      display: flex;
      flex-direction: column;

      @include breakpoint("tablet") {
        flex: 1;

        &::after {
          content: '';
          height: 100%;
          border-right: 1px solid $gray_bgr;
          border-top: 1px solid $gray_lu;
        }

        &:last-child::after {
          border-right: 0;
        }
      }

      &_selected {
        border-radius: 4px 4px 0 0;
        background: rgba(26, 120, 187, .2);
        cursor: default;
      }
    }

    &__widget-bonus-item {
      margin-bottom: 15px;
    }

    &__widget-month-wrap {
      display: flex;
      min-height: 115px;

      @include breakpoint("tablet") {
        flex: 1;
      }
    }

    &__widget-month {
      display: flex;
    }

    &__widget-day-title {
      background: $white;
      border-bottom: 1px solid $gray_lu;
      padding-top: 2px;

      @include breakpoint("tablet") {
        background: none;
        border-bottom: 0;
        padding-bottom: 9px;
        padding-left: 1px;

        &_last-day {
          border-right: 1px solid $gray_lu;
        }
      }
    }
  }
</style>
